/* 가이드 발행 아티팩트 빌더 — 순수(S3 I/O 없음). guideRepo(I/O)와 node 검증이 공유한다.
   초안 트리 + 발행본 → {version, manifest, contentById, changed, removed}. DESIGN §2/§3/§5. */
import { canonical, sha256Hex, smallHash, collectImageKeys, sanitizeBlocks } from '../../data/guideHash.js';

export const contentKey = (id) => `official/guide/content/${id}.json`;

/* 발행본 manifest 의 작은 주제를 id→entry(hash·updated_at 등)로 평탄화. */
export function publishedTopicMap(published) {
  const map = {};
  for (const cat of (published && published.categories) || [])
    for (const big of cat.big || [])
      for (const sm of big.small || []) map[sm.id] = sm;
  return map;
}

export function draftSmallIds(draftTree) {
  const ids = [];
  for (const cat of draftTree || [])
    for (const big of cat.big || [])
      for (const sm of big.small || []) ids.push(sm.id);
  return ids;
}

/* 레거시 가드 — image 블록이 .key 없이 .url 만 있으면(G2 이전 데이터) 발행 차단(다시 삽입 안내). */
function assertImagesHaveKeys(small, langs) {
  const content = small.content || {};
  const codes = new Set(Object.keys(content));
  for (const l of langs || []) if (l && l.code) codes.add(l.code);
  for (const lang of codes) {
    const arr = Array.isArray(content[lang]) ? content[lang] : [];
    for (const b of arr) {
      if (b && b.type === 'image' && !b.key) {
        const name = (small.title && (small.title.ko || Object.values(small.title)[0])) || small.id;
        throw new Error(`이미지가 업로드되지 않았습니다 — 이미지를 다시 삽입해 주세요. (작은 주제 "${name}")`);
      }
    }
  }
}

/* 레거시/미완 가드 — huenit 첨부 블록이 .key 없이 삽입만 된 경우(업로드 미완) 발행 차단(이미지 가드 동형). */
function assertHuenitHaveKeys(small, langs) {
  const content = small.content || {};
  const codes = new Set(Object.keys(content));
  for (const l of langs || []) if (l && l.code) codes.add(l.code);
  for (const lang of codes) {
    const arr = Array.isArray(content[lang]) ? content[lang] : [];
    for (const b of arr) {
      if (b && b.type === 'huenit' && !b.key) {
        const name = (small.title && (small.title.ko || Object.values(small.title)[0])) || small.id;
        throw new Error(`.huenit 첨부가 업로드되지 않았습니다 — 파일을 다시 선택해 주세요. (작은 주제 "${name}")`);
      }
    }
  }
}

/* 작은 주제 → content/{id}.json 본문(DESIGN §2.3): {id,hash,title,content}. 블록은 계약 필드만(정규화). */
function buildContentDoc(small, hash, langs) {
  const content = small.content || {};
  const codes = new Set(Object.keys(content));
  for (const l of langs || []) if (l && l.code) codes.add(l.code);
  const out = {};
  for (const lang of Array.from(codes).sort()) out[lang] = sanitizeBlocks(content[lang]);
  return { id: small.id, hash, title: small.title || {}, content: out };
}

/* 발행 UX 표식 — 작은 주제별 NEW|CHANGED|CURRENT + REMOVED(발행본엔 있으나 draft 에 없는 id). */
export async function diffDraftVsPublished(draftTree, langs, published) {
  const pubMap = publishedTopicMap(published);
  const byId = {};
  let neww = 0;
  let changed = 0;
  for (const cat of draftTree || [])
    for (const big of cat.big || [])
      for (const sm of big.small || []) {
        const h = await smallHash(sm, langs);
        const prev = pubMap[sm.id];
        let state;
        if (prev == null) { state = 'NEW'; neww++; }
        else if (prev.hash !== h) { state = 'CHANGED'; changed++; }
        else state = 'CURRENT';
        byId[sm.id] = state;
      }
  const draftIds = new Set(draftSmallIds(draftTree));
  const removed = Object.keys(pubMap).filter((id) => !draftIds.has(id));
  return { byId, removed, counts: { neww, changed } };
}

/* 발행 아티팩트 빌드(순수). nowIso = 발행 시각, published = 현재 발행본(updated_at 보존·변경 판정).
   반환: {version, manifest(DESIGN §2.2), contentById(NEW|CHANGED 본문), changed:[ids], removed:[ids]}. */
export async function buildArtifacts(draftTree, langs, published, nowIso) {
  const pubMap = publishedTopicMap(published);

  // 1) 작은 주제별 hash + 본문 + 크기/타임스탬프 + 변경 판정 (+ 레거시 이미지 가드).
  const smallMeta = {};
  const changed = [];
  for (const cat of draftTree || [])
    for (const big of cat.big || [])
      for (const sm of big.small || []) {
        assertImagesHaveKeys(sm, langs);
        assertHuenitHaveKeys(sm, langs);
        const hash = await smallHash(sm, langs);
        const prev = pubMap[sm.id];
        const isChanged = !prev || prev.hash !== hash;
        const doc = buildContentDoc(sm, hash, langs);
        const bytes = new TextEncoder().encode(JSON.stringify(doc)).length;
        const updated_at = isChanged ? nowIso : (prev && prev.updated_at) || nowIso;
        smallMeta[sm.id] = { hash, doc, bytes, updated_at };
        if (isChanged) changed.push(sm.id);
      }

  // 2) manifest — categories→big→small, 해시 롤업(자식 해시 연결 순서로 순서 반영), 전역 version.
  const categories = [];
  const catHashes = [];
  for (let ci = 0; ci < (draftTree || []).length; ci++) {
    const cat = draftTree[ci];
    const bigs = [];
    const bigHashes = [];
    for (let bi = 0; bi < (cat.big || []).length; bi++) {
      const big = cat.big[bi];
      const smalls = [];
      const smallHashes = [];
      for (let si = 0; si < (big.small || []).length; si++) {
        const sm = big.small[si];
        const meta = smallMeta[sm.id];
        smallHashes.push(meta.hash);
        const entry = {
          id: sm.id,
          order: si,
          title: sm.title || {},
          hash: meta.hash,
          content_key: contentKey(sm.id),
          images: collectImageKeys(sm, langs),
          bytes: meta.bytes,
          updated_at: meta.updated_at,
        };
        if (sm.min_app_version) entry.min_app_version = sm.min_app_version;  // passthrough(선택)
        smalls.push(entry);
      }
      const bHash = await sha256Hex(canonical({ icon: big.icon || '', label: big.label || {} }) + smallHashes.join(''));
      bigHashes.push(bHash);
      bigs.push({ id: big.id, order: bi, icon: big.icon || '', label: big.label || {}, hash: bHash, small: smalls });
    }
    const cHash = await sha256Hex(canonical({ color: cat.color || '', label: cat.label || {} }) + bigHashes.join(''));
    catHashes.push(cHash);
    const catEntry = { id: cat.id, order: ci, color: cat.color || '', label: cat.label || {}, hash: cHash, big: bigs };
    if (cat.min_app_version) catEntry.min_app_version = cat.min_app_version;  // passthrough(선택)
    categories.push(catEntry);
  }
  const version = await sha256Hex(catHashes.join(''));
  const manifest = { schema: 1, version, published_at: nowIso, categories };

  const contentById = {};
  for (const id of changed) contentById[id] = smallMeta[id].doc;

  const draftIds = new Set(draftSmallIds(draftTree));
  const removed = Object.keys(pubMap).filter((id) => !draftIds.has(id));

  return { version, manifest, contentById, changed, removed };
}
